import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useInsertData } from "../../hooks/useInsertData";
import { useGetData } from "../../hooks/useGetData";
import { useUpdateData } from "../../hooks/useUpdateDate";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";

export const createUser = createAsyncThunk("auth/createUser", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/signup", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const vertifyEmail = createAsyncThunk("auth/verifyEmail", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/client/verify-email", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const loginUser = createAsyncThunk("auth/loginUser", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/signin", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const getCurrentUser = createAsyncThunk("auth/getCurrentUser", async (role, { rejectWithValue }) => {
  try {
    let endpoint;
    if (role === "employee") {
      endpoint = "employees/showCurrent";
    } else if (role === "client") {
      endpoint = "clients/showCurrent";
    } else if (role === "admin") {
      endpoint = "admins/showCurrent";
    } else if (role === "commissioner") {
      endpoint = "commissioner/showCurrent";
    }
    const res = await useGetData(endpoint);
    return { role, user: res.data };
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const resendCode = createAsyncThunk("auth/resendCode", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/client/resend-code", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const forgetPassword = createAsyncThunk("auth/forgetPassword", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/forgot-password", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const resetPassword = createAsyncThunk("auth/resetPassword", async (data, { rejectWithValue }) => {
  try {
    const res = await useInsertData("auth/reset-password-email", data);
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});
export const logoutClient = createAsyncThunk("auth/logoutClient", async (_, { rejectWithValue }) => {
  try {
    const res = await useGetData("auth/client/logout");
    return res.data;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

export const updateUserData = createAsyncThunk("auth/updateClient", async ({ data, role }, { rejectWithValue, dispatch }) => {
  const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
  try {
    const endpoint = role === "employee" ? `employees/${user.id}` : `clients/${user.id}`;
    await useUpdateData(endpoint, data);
    const res = await dispatch(getCurrentUser(role));
    return res.payload;
  } catch (err) {
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err.message);
  }
});

const initialState = {
  user: null,
  verifyEmailResponse: null,
  forgetPasswordResponse: null,
  resetPasswordResponse: null,
  currentUser: null,
  resendCodeResponse: null,
  verifyPhoneResponse: null,
  updatePasswordResponse: null,
  status: "idle",
  error: null,
  token: null,
};
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.currentUser = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      state.token = null;
      window.location.href = "/SignIn";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(vertifyEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(vertifyEmail.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.verifyEmailResponse = action.payload;
      })
      .addCase(vertifyEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(resendCode.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resendCode.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.resendCodeResponse = action.payload;
      })
      .addCase(resendCode.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload.data;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(getCurrentUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload.user;
        state.status = "succeeded";
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(forgetPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(forgetPassword.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.forgetPasswordResponse = action.payload;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.resetPasswordResponse = action.payload;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      //---------------------------------------
      .addCase(updateUserData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateUserData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
      })
      .addCase(updateUserData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});
export const { logoutUser } = authSlice.actions;
export default authSlice.reducer;
